import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { Tractor } from "lucide-react";
import { structureToken, BED_TOKEN } from "./structure-types";

/**
 * A single draggable item on the garden map (either a Bed or a Structure).
 *
 * Position + size are in CM; we convert to px via the `scale` prop.
 * On drag end, the parent reads `event.delta` (pixels), divides by `scale`,
 * and snaps to a 10 cm grid before persisting via the API.
 */
export function MapItem({ kind, item, scale, selected, onClick }) {
    const id = `${kind}-${item.id}`;
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id,
        data: { kind, itemId: item.id, item },
    });

    const token = kind === "bed" ? BED_TOKEN : structureToken(item.type);
    const Icon = kind === "bed" ? Tractor : token.icon;

    // Render at scaled (px) position + size
    const left = (item.position_x ?? 0) * scale;
    const top = (item.position_y ?? 0) * scale;
    const width = Math.max(40, (item.length_cm ?? 200) * scale);
    const height = Math.max(28, (item.width_cm ?? 200) * scale);

    const style = {
        position: "absolute",
        left,
        top,
        width,
        height,
        transform: CSS.Translate.toString(transform),
        zIndex: isDragging ? 50 : selected ? 20 : 10,
        opacity: isDragging ? 0.85 : 1,
        boxShadow: isDragging
            ? "0 8px 24px rgba(60,50,30,0.18)"
            : selected
                ? "0 0 0 3px var(--vm-accent), 0 4px 12px rgba(60,50,30,0.12)"
                : "0 1px 4px rgba(60,50,30,0.08)",
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            data-testid={`map-${kind}-${item.id}`}
            data-kind={kind}
            className={`select-none touch-none cursor-grab active:cursor-grabbing rounded-md border ${token.bg} ${token.border} ${token.text} flex items-center justify-center gap-1 px-1 transition-shadow`}
            onClick={(e) => { e.stopPropagation(); onClick?.(); }}
            {...listeners}
            {...attributes}
        >
            <Icon className="w-3 h-3 shrink-0 opacity-80" strokeWidth={1.5} />
            <span className="text-[10px] font-medium truncate" title={item.name}>
                {item.name}
            </span>
        </div>
    );
}
